import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { StatusCounts } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface StripeStatusPieChartProps {
  data: StatusCounts;
}

const STATUS_COLORS = {
  paid: '#10b981', // emerald-500
  failed: '#ef4444', // red-500
  refunded: '#f59e0b', // amber-500
  other: '#6b7280', // gray-500
};

const StripeStatusPieChart = ({ data }: StripeStatusPieChartProps) => {
  const chartData = [
    { name: 'Paid', value: data.paid, color: STATUS_COLORS.paid },
    { name: 'Failed', value: data.failed, color: STATUS_COLORS.failed },
    { name: 'Refunded', value: data.refunded, color: STATUS_COLORS.refunded },
    { name: 'Other', value: data.other, color: STATUS_COLORS.other },
  ].filter((item) => item.value > 0);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <Card className='h-full'>
        <CardHeader>
          <CardTitle className='text-lg'>Transaction Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[220px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Add total to each item for percentage calculation in tooltip
  const dataWithTotal = chartData.map((item) => ({ ...item, total }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    const percent = ((item.value / total) * 100).toFixed(1);
    return (
      <div className='bg-white p-3 border rounded shadow-lg'>
        <p className='font-medium' style={{ color: item.payload.color }}>
          {item.name}
        </p>
        <p className='text-sm text-muted-foreground'>
          {item.value.toLocaleString()} ({percent}%)
        </p>
      </div>
    );
  };

  return (
    <Card className='h-full'>
      <CardHeader>
        <CardTitle className='text-lg'>Transaction Status</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col md:flex-row gap-4 items-center'>
          {/* Pie Chart */}
          <div className='flex-shrink-0'>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={dataWithTotal}
                  cx='50%'
                  cy='50%'
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className='flex-1 space-y-2'>
            {chartData.map((item, index) => {
              const percent = ((item.value / total) * 100).toFixed(1);
              return (
                <div key={index} className='flex items-center gap-2 text-sm'>
                  <div
                    className='w-3 h-3 rounded-sm flex-shrink-0'
                    style={{ backgroundColor: item.color }}
                  />
                  <span className='flex-1'>{item.name}</span>
                  <span className='text-muted-foreground'>
                    {item.value.toLocaleString()} ({percent}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StripeStatusPieChart;
